import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { alumnosProfesorService } from "../../../services/profesor/alumnos.service";
import { simulacionesProfesorService } from "../../../services/profesor/simulaciones.service";
import { AlumnoProfesor } from "../alumnos/dto/alumnos.dto";
import { ProfesorLayout } from "../components/ProfesorLayout";
import {
  FiltrosSimulaciones,
  SimulacionProfesor,
} from "./dto/simulaciones.dto";

const COLORS = {
  correctas: "#10b981", // green-500
  incorrectas: "#ef4444", // red-500
  primary: "#8b5cf6", // purple-500
  secondary: "#ec4899", // pink-500
};

const COLORS_PIE = ["#10b981", "#f59e0b", "#ef4444"];

export const SimulacionesPage: React.FC = () => {
  const [filtros, setFiltros] = useState<FiltrosSimulaciones>({});
  const [alumnoSeleccionado, setAlumnoSeleccionado] = useState<number | null>(
    null
  );

  // Obtener alumnos para el filtro
  const { data: alumnos = [], error: errorAlumnos } = useQuery<
    AlumnoProfesor[]
  >({
    queryKey: ["alumnos-profesor"],
    queryFn: () => alumnosProfesorService.getAlumnos(),
  });

  React.useEffect(() => {
    if (errorAlumnos) {
      console.error("Error cargando alumnos:", errorAlumnos);
    }
  }, [errorAlumnos]);

  // Obtener simulaciones
  const {
    data: simulaciones = [],
    isLoading: loadingSimulaciones,
    error: errorSimulaciones,
  } = useQuery<SimulacionProfesor[]>({
    queryKey: ["simulaciones-profesor", filtros],
    queryFn: () => simulacionesProfesorService.getSimulaciones(filtros),
  });

  React.useEffect(() => {
    if (errorSimulaciones) {
      console.error("Error cargando simulaciones:", errorSimulaciones);
    }
  }, [errorSimulaciones]);

  // Las estadísticas ahora se calculan desde las simulaciones filtradas
  const loadingEstadisticas = loadingSimulaciones;

  // Obtener estadísticas del alumno seleccionado
  const { data: estadisticasAlumno, error: errorEstadisticas } = useQuery({
    queryKey: ["estadisticas-alumno", alumnoSeleccionado],
    queryFn: () =>
      alumnoSeleccionado
        ? simulacionesProfesorService.getEstadisticasAlumno(alumnoSeleccionado)
        : null,
    enabled: !!alumnoSeleccionado,
  });

  React.useEffect(() => {
    if (errorEstadisticas) {
      console.error(
        "Error cargando estadísticas del alumno:",
        errorEstadisticas
      );
    }
  }, [errorEstadisticas]);

  const handleFiltroChange = (key: keyof FiltrosSimulaciones, value: any) => {
    const newFiltros = {
      ...filtros,
      [key]: value || undefined,
    };
    setFiltros(newFiltros);

    // Si se filtra por alumno, actualizar el estado
    if (key === "alumnoId") {
      if (value) {
        setAlumnoSeleccionado(parseInt(value));
      } else {
        setAlumnoSeleccionado(null);
      }
    }
  };

  const handleVerAlumno = (alumnoId: number) => {
    setAlumnoSeleccionado(alumnoId);
    setFiltros((prev) => ({ ...prev, alumnoId }));
  };

  const handleVolverGeneral = () => {
    setAlumnoSeleccionado(null);
    setFiltros((prev) => {
      const { alumnoId, ...rest } = prev;
      return rest;
    });
  };

  // Calcular estadísticas desde simulaciones filtradas
  const estadisticasFiltradas = React.useMemo(() => {
    if (!simulaciones || simulaciones.length === 0) return null;

    const totalSimulaciones = simulaciones.length;
    const simulacionesFinalizadas = simulaciones.filter(
      (s) => s.estado === "FINALIZADA"
    ).length;
    const simulacionesEnCurso = simulaciones.filter(
      (s) => s.estado === "EN_CURSO"
    ).length;

    let totalCorrectas = 0;
    let totalIncorrectas = 0;
    const simulacionesConDatos = simulaciones.filter(
      (s) => s.totalPreguntas > 0
    );

    simulacionesConDatos.forEach((sim) => {
      totalCorrectas += sim.correctas;
      totalIncorrectas += sim.incorrectas;
    });

    const promedioCorrectas =
      simulacionesConDatos.length > 0
        ? totalCorrectas / simulacionesConDatos.length
        : 0;
    const promedioIncorrectas =
      simulacionesConDatos.length > 0
        ? totalIncorrectas / simulacionesConDatos.length
        : 0;

    const totalPreguntas = totalCorrectas + totalIncorrectas;
    const promedioPorcentaje =
      totalPreguntas > 0 ? (totalCorrectas / totalPreguntas) * 100 : 0;

    // Agrupar por alumno
    const simulacionesPorAlumnoMap = new Map<
      number,
      {
        alumnoId: number;
        nombreCompleto: string;
        simulaciones: typeof simulaciones;
      }
    >();

    simulaciones.forEach((sim) => {
      const key = sim.alumno.idUsuario;
      if (!simulacionesPorAlumnoMap.has(key)) {
        simulacionesPorAlumnoMap.set(key, {
          alumnoId: sim.alumno.idUsuario,
          nombreCompleto: sim.alumno.nombreCompleto,
          simulaciones: [],
        });
      }
      simulacionesPorAlumnoMap.get(key)!.simulaciones.push(sim);
    });

    const simulacionesPorAlumno = Array.from(
      simulacionesPorAlumnoMap.values()
    ).map((item) => {
      let totalCorrectasAlumno = 0;
      let totalIncorrectasAlumno = 0;
      let totalSimulacionesAlumno = 0;

      item.simulaciones.forEach((sim) => {
        if (sim.totalPreguntas > 0) {
          totalCorrectasAlumno += sim.correctas;
          totalIncorrectasAlumno += sim.incorrectas;
          totalSimulacionesAlumno++;
        }
      });

      const promedioCorrectasAlumno =
        totalSimulacionesAlumno > 0
          ? totalCorrectasAlumno / totalSimulacionesAlumno
          : 0;
      const promedioIncorrectasAlumno =
        totalSimulacionesAlumno > 0
          ? totalIncorrectasAlumno / totalSimulacionesAlumno
          : 0;
      const totalPreguntasAlumno =
        totalCorrectasAlumno + totalIncorrectasAlumno;
      const promedioPorcentajeAlumno =
        totalPreguntasAlumno > 0
          ? (totalCorrectasAlumno / totalPreguntasAlumno) * 100
          : 0;

      return {
        alumnoId: item.alumnoId,
        nombreCompleto: item.nombreCompleto,
        totalSimulaciones: item.simulaciones.length,
        promedioCorrectas: Math.round(promedioCorrectasAlumno * 100) / 100,
        promedioIncorrectas: Math.round(promedioIncorrectasAlumno * 100) / 100,
        promedioPorcentaje: Math.round(promedioPorcentajeAlumno * 100) / 100,
      };
    });

    // Agrupar por fecha
    const simulacionesPorFechaMap = new Map<string, typeof simulaciones>();

    simulaciones.forEach((sim) => {
      const fechaStr = new Date(sim.fecha).toISOString().split("T")[0];
      if (!simulacionesPorFechaMap.has(fechaStr)) {
        simulacionesPorFechaMap.set(fechaStr, []);
      }
      simulacionesPorFechaMap.get(fechaStr)!.push(sim);
    });

    const simulacionesPorFecha = Array.from(simulacionesPorFechaMap.entries())
      .map(([fecha, sims]) => {
        let totalCorrectasFecha = 0;
        let totalIncorrectasFecha = 0;
        let cantidadConDatos = 0;

        sims.forEach((sim) => {
          if (sim.totalPreguntas > 0) {
            totalCorrectasFecha += sim.correctas;
            totalIncorrectasFecha += sim.incorrectas;
            cantidadConDatos++;
          }
        });

        return {
          fecha,
          cantidad: sims.length,
          promedioCorrectas:
            cantidadConDatos > 0
              ? Math.round((totalCorrectasFecha / cantidadConDatos) * 100) / 100
              : 0,
          promedioIncorrectas:
            cantidadConDatos > 0
              ? Math.round((totalIncorrectasFecha / cantidadConDatos) * 100) /
                100
              : 0,
        };
      })
      .sort((a, b) => a.fecha.localeCompare(b.fecha));

    return {
      totalSimulaciones,
      simulacionesFinalizadas,
      simulacionesEnCurso,
      promedioCorrectas: Math.round(promedioCorrectas * 100) / 100,
      promedioIncorrectas: Math.round(promedioIncorrectas * 100) / 100,
      promedioPorcentaje: Math.round(promedioPorcentaje * 100) / 100,
      simulacionesPorAlumno,
      simulacionesPorFecha,
    };
  }, [simulaciones]);

  // Preparar datos para gráficas usando estadísticas filtradas
  const datosCorrectasIncorrectas = estadisticasFiltradas
    ? [
        {
          name: "Correctas",
          cantidad: estadisticasFiltradas.promedioCorrectas,
          color: COLORS.correctas,
        },
        {
          name: "Incorrectas",
          cantidad: estadisticasFiltradas.promedioIncorrectas,
          color: COLORS.incorrectas,
        },
      ]
    : [];

  const datosPorAlumno =
    estadisticasFiltradas?.simulacionesPorAlumno.map((item) => ({
      nombre: item.nombreCompleto.split(" ")[0], // Solo primer nombre
      correctas: item.promedioCorrectas,
      incorrectas: item.promedioIncorrectas,
      porcentaje: item.promedioPorcentaje,
    })) || [];

  const datosPorFecha =
    estadisticasFiltradas?.simulacionesPorFecha.map((item) => ({
      fecha: new Date(item.fecha).toLocaleDateString("es-CL", {
        month: "short",
        day: "numeric",
      }),
      cantidad: item.cantidad,
      promedioCorrectas: item.promedioCorrectas,
      promedioIncorrectas: item.promedioIncorrectas,
    })) || [];

  const datosEvolucionAlumno =
    estadisticasAlumno?.evolucionPorFecha.map((item) => ({
      fecha: new Date(item.fecha).toLocaleDateString("es-CL", {
        month: "short",
        day: "numeric",
      }),
      porcentaje: item.porcentaje,
      correctas: item.correctas,
      incorrectas: item.incorrectas,
    })) || [];

  const datosEstadoSimulaciones = estadisticasFiltradas
    ? [
        {
          name: "Finalizadas",
          value: estadisticasFiltradas.simulacionesFinalizadas,
        },
        {
          name: "En Curso",
          value: estadisticasFiltradas.simulacionesEnCurso,
        },
        {
          name: "Canceladas",
          value:
            estadisticasFiltradas.totalSimulaciones -
            estadisticasFiltradas.simulacionesFinalizadas -
            estadisticasFiltradas.simulacionesEnCurso,
        },
      ]
    : [];

  return (
    <ProfesorLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header mejorado */}
        <div className="mb-10">
          <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-xl shadow-lg p-8 text-white">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-4xl font-bold mb-2">Simulaciones PAES</h1>
                <p className="text-purple-100 text-lg">
                  Gestión y análisis de simulaciones de tus estudiantes
                </p>
              </div>
              {alumnoSeleccionado && (
                <button
                  onClick={handleVolverGeneral}
                  className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white rounded-lg font-semibold transition-all duration-200 backdrop-blur-sm border border-white/30"
                >
                  ← Ver todas las simulaciones
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Filtros mejorados */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <svg
              className="w-5 h-5 text-purple-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            Filtros
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Alumno
              </label>
              <select
                value={filtros.alumnoId || ""}
                onChange={(e) =>
                  handleFiltroChange(
                    "alumnoId",
                    e.target.value ? parseInt(e.target.value) : null
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Todos los alumnos</option>
                {alumnos.map((alumno) => (
                  <option key={alumno.idUsuario} value={alumno.idUsuario}>
                    {alumno.nombreCompleto}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estado
              </label>
              <select
                value={filtros.estado || ""}
                onChange={(e) =>
                  handleFiltroChange("estado", e.target.value || null)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Todos los estados</option>
                <option value="FINALIZADA">Finalizadas</option>
                <option value="EN_CURSO">En Curso</option>
                <option value="CANCELADA">Canceladas</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha Desde
              </label>
              <input
                type="date"
                value={filtros.fechaDesde || ""}
                onChange={(e) =>
                  handleFiltroChange("fechaDesde", e.target.value || null)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha Hasta
              </label>
              <input
                type="date"
                value={filtros.fechaHasta || ""}
                onChange={(e) =>
                  handleFiltroChange("fechaHasta", e.target.value || null)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Estadísticas Generales o del Alumno */}
        {alumnoSeleccionado && !estadisticasAlumno && !loadingSimulaciones ? (
          <div className="bg-white rounded-lg shadow-sm p-12 border border-gray-200 text-center">
            <div className="max-w-md mx-auto">
              <div className="mb-4">
                <svg
                  className="mx-auto h-16 w-16 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No hay información disponible
              </h3>
              <p className="text-gray-600">
                El estudiante seleccionado no tiene simulaciones registradas
                aún.
              </p>
            </div>
          </div>
        ) : alumnoSeleccionado && estadisticasAlumno ? (
          <div className="space-y-6">
            {/* Cards de estadísticas del alumno mejoradas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
                <p className="text-sm font-semibold text-gray-600 mb-2">
                  Total Simulaciones
                </p>
                <p className="text-4xl font-bold text-gray-900">
                  {estadisticasAlumno.totalSimulaciones}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
                <p className="text-sm font-semibold text-gray-600 mb-2">
                  Promedio Correctas
                </p>
                <p className="text-4xl font-bold text-green-600">
                  {estadisticasAlumno.promedioCorrectas.toFixed(1)}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
                <p className="text-sm font-semibold text-gray-600 mb-2">
                  Promedio Incorrectas
                </p>
                <p className="text-4xl font-bold text-red-600">
                  {estadisticasAlumno.promedioIncorrectas.toFixed(1)}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
                <p className="text-sm font-semibold text-gray-600 mb-2">
                  Promedio % Correctas
                </p>
                <p className="text-4xl font-bold text-purple-600">
                  {estadisticasAlumno.promedioPorcentaje.toFixed(1)}%
                </p>
              </div>
            </div>

            {/* Gráficas del alumno */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Evolución del Rendimiento
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={datosEvolucionAlumno}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="fecha" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="porcentaje"
                      stroke={COLORS.primary}
                      strokeWidth={2}
                      name="% Correctas"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Correctas vs Incorrectas por Fecha
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={datosEvolucionAlumno}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="fecha" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="correctas"
                      fill={COLORS.correctas}
                      name="Correctas"
                    />
                    <Bar
                      dataKey="incorrectas"
                      fill={COLORS.incorrectas}
                      name="Incorrectas"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Tabla de simulaciones del alumno */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Historial de Simulaciones -{" "}
                  {estadisticasAlumno.alumno.nombreCompleto}
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fecha
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Preguntas
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Correctas
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Incorrectas
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        % Correctas
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tiempo (min)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {estadisticasAlumno.simulaciones.map((sim) => (
                      <tr
                        key={sim.idSimulacionPAES}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(sim.fecha).toLocaleDateString("es-CL", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              sim.estado === "FINALIZADA"
                                ? "bg-green-100 text-green-800"
                                : sim.estado === "EN_CURSO"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {sim.estado}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {sim.totalPreguntas}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                          {sim.correctas}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-600">
                          {sim.incorrectas}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-purple-600">
                          {sim.porcentajeCorrectas.toFixed(1)}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {sim.tiempoTotal ? `${sim.tiempoTotal} min` : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Cards de estadísticas generales */}
            {loadingEstadisticas ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Cargando estadísticas...</p>
              </div>
            ) : !loadingSimulaciones && simulaciones.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-12 border border-gray-200 text-center">
                <div className="max-w-md mx-auto">
                  <div className="mb-4">
                    <svg
                      className="mx-auto h-16 w-16 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    No hay información disponible
                  </h3>
                  <p className="text-gray-600">
                    {filtros.alumnoId
                      ? "El estudiante seleccionado no tiene simulaciones que coincidan con los filtros aplicados."
                      : "No hay simulaciones que coincidan con los filtros aplicados."}
                  </p>
                </div>
              </div>
            ) : estadisticasFiltradas ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                  <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
                    <p className="text-sm font-semibold text-gray-600 mb-2">
                      Total Simulaciones
                    </p>
                    <p className="text-4xl font-bold text-gray-900">
                      {estadisticasFiltradas.totalSimulaciones}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
                    <p className="text-sm font-semibold text-gray-600 mb-2">
                      Finalizadas
                    </p>
                    <p className="text-4xl font-bold text-green-600">
                      {estadisticasFiltradas.simulacionesFinalizadas}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
                    <p className="text-sm font-semibold text-gray-600 mb-2">
                      Promedio Correctas
                    </p>
                    <p className="text-4xl font-bold text-green-600">
                      {estadisticasFiltradas.promedioCorrectas.toFixed(1)}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
                    <p className="text-sm font-semibold text-gray-600 mb-2">
                      Promedio % Correctas
                    </p>
                    <p className="text-4xl font-bold text-purple-600">
                      {estadisticasFiltradas.promedioPorcentaje.toFixed(1)}%
                    </p>
                  </div>
                </div>

                {/* Gráficas generales */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Promedio Correctas vs Incorrectas
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={datosCorrectasIncorrectas}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                          dataKey="name"
                          tick={{
                            fill: "#374151",
                            fontSize: 14,
                            fontWeight: 500,
                          }}
                        />
                        <YAxis tick={{ fill: "#374151", fontSize: 14 }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#fff",
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                            fontSize: "14px",
                          }}
                        />
                        <Bar
                          dataKey="cantidad"
                          fill={COLORS.primary}
                          radius={[8, 8, 0, 0]}
                        >
                          {datosCorrectasIncorrectas.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Estado de Simulaciones
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={datosEstadoSimulaciones}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent, value }) => {
                            if (value === 0) return "";
                            return `${name}: ${
                              percent ? (percent * 100).toFixed(0) : 0
                            }%`;
                          }}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                          fontSize={14}
                          fontWeight={600}
                        >
                          {datosEstadoSimulaciones.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS_PIE[index % COLORS_PIE.length]}
                              stroke="#fff"
                              strokeWidth={2}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#fff",
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                            fontSize: "14px",
                          }}
                          formatter={(value: any) => [
                            `${value} simulaciones`,
                            "Cantidad",
                          ]}
                        />
                        <Legend
                          wrapperStyle={{ fontSize: "14px", fontWeight: 500 }}
                          iconType="circle"
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Rendimiento por Alumno
                    </h3>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={datosPorAlumno} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 100]} />
                        <YAxis dataKey="nombre" type="category" width={100} />
                        <Tooltip />
                        <Legend />
                        <Bar
                          dataKey="porcentaje"
                          fill={COLORS.primary}
                          name="% Correctas"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Simulaciones por Fecha
                    </h3>
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={datosPorFecha}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="fecha" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="cantidad"
                          stroke={COLORS.primary}
                          strokeWidth={2}
                          name="Cantidad"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Tabla de simulaciones */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="p-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Todas las Simulaciones ({simulaciones.length})
                    </h3>
                  </div>
                  {loadingSimulaciones ? (
                    <div className="p-12 text-center text-gray-500">
                      Cargando simulaciones...
                    </div>
                  ) : simulaciones.length === 0 ? (
                    <div className="p-12 text-center text-gray-500">
                      No hay simulaciones que mostrar con los filtros
                      seleccionados
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Alumno
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Fecha
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Estado
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Total
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Correctas
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Incorrectas
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              % Correctas
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Acciones
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {simulaciones.map((sim) => (
                            <tr
                              key={sim.idSimulacionPAES}
                              className="hover:bg-gray-50"
                            >
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {sim.alumno.nombreCompleto}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {new Date(sim.fecha).toLocaleDateString(
                                  "es-CL",
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  }
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                    sim.estado === "FINALIZADA"
                                      ? "bg-green-100 text-green-800"
                                      : sim.estado === "EN_CURSO"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-red-100 text-red-800"
                                  }`}
                                >
                                  {sim.estado}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {sim.totalPreguntas}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                                {sim.correctas}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-600">
                                {sim.incorrectas}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-purple-600">
                                {sim.porcentajeCorrectas.toFixed(1)}%
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <button
                                  onClick={() =>
                                    handleVerAlumno(sim.alumno.idUsuario)
                                  }
                                  className="text-purple-600 hover:text-purple-800 font-medium"
                                >
                                  Ver detalle
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>
    </ProfesorLayout>
  );
};

export default SimulacionesPage;
